import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  getValue,
  TranslateModule,
  TranslateService,
  TranslateStore,
} from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import {
  GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
  SKILL_MD_TRAITS,
} from '@shared';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, firstValueFrom, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import * as fromGenerated from '../../../_generated';
import { type SupportedLanguage } from '../../../app.i18n';
import * as fromAuth from '../../../auth/store/auth.reducer';
import { ConfirmDialogService } from '../../../confirm-dialog/confirm-dialog.service';
import * as fromFavorites from '../../../favorites';
import * as fromFeatureFlags from '../../../feature-flags';
import * as fromLayout from '../../../layout/store/layout.reducer';
import { sentryCaptureError } from '../../../sentry.helper';
import {
  createFileUploadV2Store,
  FileUploadV2Store,
} from '../../shared/directives/file-upload-v2';
import { ASSISTANT_CUSTOM_ICONS } from '../assistants.icon.helper';
import {
  accessToShareOption,
  AssistantShareSectionComponent,
  ShareVisibility,
} from '../share/assistant-share-section.component';
import {
  AssistantActions,
  AssistantBuilderModalActions,
  AssistantsActions,
} from '../store/assistants.actions';
import { AssistantsState } from '../store/assistants.reducer';
import {
  AssistantBuilderBasicFormComponent,
  AssistantBuilderBasicFormValues,
  BuilderLocale,
} from './assistant-builder-basic-form.component';
import { AssistantBuilderFileValue } from './assistant-builder-file-upload.component';
import {
  AssistantBuilderModelSelectorComponent,
  ModelOption,
} from './assistant-builder-model-selector.component';
import {
  AssistantBuilderTraitComparisonFormComponent,
  AssistantBuilderTraitComparisonFormValues,
} from './assistant-builder-trait-comparison-form.component';
import {
  AssistantBuilderTraitComplianceFormComponent,
  AssistantBuilderTraitComplianceFormValues,
} from './assistant-builder-trait-compliance-form.component';
import { AssistantBuilderTraitCreativeFormComponent } from './assistant-builder-trait-creative-form.component';
import { AssistantBuilderTraitGenericFormComponent } from './assistant-builder-trait-generic-form.component';
import { AssistantBuilderTraitGenericFormValues } from './assistant-builder-trait-generic-form.validators';
import { AssistantBuilderTraitInsightFormComponent } from './assistant-builder-trait-insight-form.component';
import {
  AssistantBuilderTypeSelectorComponent,
  TypeOption,
  TypeOptions,
} from './assistant-builder-type-selector.component';
import {
  hasRemovePromptInput,
  isComplianceConfig,
  isComplianceConfigTranslation,
} from './assistant-builder.helper';
import {
  DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_UPDATE_MAX_LENGTH,
  isValidTranslation,
} from './assistant-builder.validators';

export type AssistantBuilderTraitForm =
  | AssistantBuilderTraitInsightFormComponent
  | AssistantBuilderTraitGenericFormComponent
  | AssistantBuilderTraitComparisonFormComponent
  | AssistantBuilderTraitComplianceFormComponent
  | AssistantBuilderTraitCreativeFormComponent;

/** Compliance form keys synced between fr/en; excludes per-locale labels. */
const COMPLIANCE_NOT_TRANSLATED_FORM_KEYS: Exclude<
  keyof AssistantBuilderTraitComplianceFormValues,
  'referenceLabel' | 'regularLabel'
>[] = [
  'removePromptInput',
  'userWillUploadReferenceDocuments',
  'configurationFile',
  'files',
] as const;

/** File chip tagged with the document role it will be persisted under. */
interface RoleTaggedFile {
  id: string;
  name: string;
  role: fromGenerated.DocumentRole;
}

@Component({
  imports: [
    FormsModule,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.ToggleComponent,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.ModalComponent,
    TranslateModule,
    AssistantBuilderTypeSelectorComponent,
    AssistantBuilderModelSelectorComponent,
    AssistantBuilderBasicFormComponent,
    AssistantBuilderTraitInsightFormComponent,
    AssistantBuilderTraitGenericFormComponent,
    AssistantBuilderTraitComparisonFormComponent,
    AssistantBuilderTraitComplianceFormComponent,
    AssistantBuilderTraitCreativeFormComponent,
    AssistantShareSectionComponent,
  ],
  selector: 'squadbox-assistant-builder',
  template: `
    <sd-modal
      [isModalVisible]="true"
      [modalOptions]="modalOptions()"
      [style.--modal-content-padding]="'0px'"
      (triggerAction)="onModalAction($event)"
    >
      <!-- Body: main column + share panel (Figma 25443:52289 modal shell). The
           two-column row owns a bounded height on desktop so the main column and
           the share panel scroll independently; on mobile it flows and the modal
           body scrolls. The height targets the D1 ~80px top/bottom margins
           (modal ≈ viewport − 160px = body + header/footer chrome); pending a
           logged-in §9 pixel-parity pass to confirm/tune. -->
      <div
        class="flex flex-col tablet-landscape:flex-row overflow-y-auto tablet-landscape:overflow-hidden hide-scrollbar tablet-landscape:h-[calc(100svh-14.5rem)]"
      >
        <!-- Main column. The 64px side padding (px-16) lives HERE, on the one
             column container — not repeated per section — so the header, the
             scrollable body, and every divider share a single inset. 32px top
             (pt-8) matches the teammate builder (Figma 25443:52477). -->
        <div
          class="grow flex flex-col min-w-0 tablet-landscape:overflow-hidden px-16"
        >
          <!-- Header: title + favorite star + divider -->
          <div class="shrink-0 pt-8">
            <div class="flex items-center gap-4">
              <!-- Saved-state title = the skill's current name (D18); New shows
                     the create title. -->
              <span
                class="typo-h6 font-semibold leading-8 text-neutral-900 grow truncate"
              >
                @if (assistant(); as skill) {
                  {{ skill.name }}
                } @else {
                  {{ 'ASSISTANTS.ASSISTANT_BUILDER.TITLE_CREATE' | translate }}
                }
              </span>
              <!-- Favorite star (SB-3325 PR-4 / Figma 25309): stageable on
                     create (applied on save), instant commit on edit (D6). -->
              <button
                sdIconButton
                variant="icon"
                [color]="isFavorite() ? 'primary' : 'neutral'"
                size="xs"
                [attr.aria-label]="
                  'ASSISTANTS.ASSISTANT_BUILDER.FAVORITE_ARIA_LABEL' | translate
                "
                [attr.aria-pressed]="isFavorite()"
                (click)="handleToggleFavorite()"
              >
                <svg
                  [sdUseUIBasicIcon]="isFavorite() ? 'star-filled' : 'star'"
                ></svg>
              </button>
            </div>
            <div class="border-t border-neutral-200 mt-8"></div>
          </div>

          <!-- Scrollable content — a flex column. Figma's pink 16px gaps
               (25443:52477) are the flex gap-4; the 24px top/bottom is py-6
               (Gap&Padding/6) and the 64px sides come from the column container.
               Each Skill Builder Divider is its own flex item carrying 8px padding
               (py-2), so content sits 16 + 8 = 24px from the line. -->
          <div
            class="grow tablet-landscape:overflow-y-auto tablet-landscape:overflow-x-hidden hide-scrollbar flex flex-col gap-4 py-6"
          >
            <!-- Type + Model visibility gate (KD-6 / SBB-03): the kept UI-only
                 ff-advanced-assistant-builder flag hides the row whenever it is
                 OFF — in CREATE and EDIT alike. trait and model are immutable
                 after create (neither is sent in UpdateAssistantDto), so hiding
                 the row on a flag-off edit cannot change them; when ON in EDIT
                 both controls stay locked to the stored values
                 ([disabled]="!!assistant()"). Both are flex-1 with min-w-0 so
                 they split the row 50/50 at every width, per Figma 25459:63618
                 (1280px) and 25443:52477 (1512px) — both show equal halves
                 (SBB-11: dropped the earlier max-w-[348px] cap on Type). -->
            @if (ffAdvancedAssistantBuilder()) {
              <div
                class="flex flex-col gap-4 tablet-landscape:flex-row tablet-landscape:gap-6"
              >
                <squadbox-assistant-builder-type-selector
                  class="w-full tablet-landscape:flex-1 tablet-landscape:min-w-0"
                  [disabled]="!!assistant()"
                  [(selectedType)]="selectedType"
                />
                <squadbox-assistant-builder-model-selector
                  class="w-full tablet-landscape:flex-1 tablet-landscape:min-w-0"
                  [disabled]="!!assistant()"
                  [options]="availableModels()"
                  [(selectedSlug)]="selectedModelSlug"
                />
              </div>
              <div class="py-2">
                <div class="border-t border-neutral-200"></div>
              </div>
            }

            <!-- Multilingual-support toggle (SB-3325 PR-4): OFF = one universal
                 locale, ON = distinct fr + en (both required). -->
            <sd-toggle
              size="sm"
              [label]="
                'ASSISTANTS.SAVE_MODAL.FORM.SUPPORT_MULTILINGUAL.LABEL'
                  | translate
              "
              [subtext]="
                'ASSISTANTS.SAVE_MODAL.FORM.SUPPORT_MULTILINGUAL.SUBTITLE'
                  | translate
              "
              [ngModel]="supportMultilingual()"
              (ngModelChange)="supportMultilingual.set($event)"
            ></sd-toggle>
            <div class="py-2">
              <div class="border-t border-neutral-200"></div>
            </div>

            <!-- Locale-aware basic form (renders per-locale rows internally) -->
            <squadbox-assistant-builder-basic-form
              #basicForm
              [locales]="activeLocales()"
              [hasIntroMessage]="true"
              [isCreateMode]="!assistant()"
              [descriptionMaxLengthByLocale]="descriptionMaxLengthByLocale()"
              [descriptionStoredValueByLocale]="
                descriptionStoredValueByLocale()
              "
            />

            <!-- Divider -->
            <div class="py-2">
              <div class="border-t border-neutral-200"></div>
            </div>
            @let type = selectedType().type;
            @switch (type) {
              @case ('insight') {
                <squadbox-assistant-builder-trait-insight-form
                  #traitForm
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                />
              }
              @case ('generic') {
                <squadbox-assistant-builder-trait-generic-form
                  #traitForm
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                />
              }
              @case ('comparison') {
                <squadbox-assistant-builder-trait-comparison-form
                  #traitForm
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                />
              }
              @case ('compliance') {
                <!-- Three independent compliance slots (KD-18/KD-21, spec §6):
                       und is the single-language (OFF) editing surface; fr + en
                       are the multilingual slots. To avoid repeating the shared
                       sections (Instructions / Reference documents / toggles),
                       only ONE slot renders a visible body at a time — und (OFF)
                       or fr (ON). The en slot stays mounted-but-hidden so its
                       FormGroup still drives save/validity/sync; its per-locale
                       Customize labels are rendered inline by the fr slot via
                       the secondaryForm input (Figma 11531:14046). -->
                <squadbox-assistant-builder-trait-compliance-form
                  #complianceFormUnd
                  [class.hidden]="supportMultilingual()"
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                  [configurationUploadStore]="complianceConfigStore"
                  [referenceUploadStore]="complianceReferenceStore"
                  [language]="currentUiLanguage()"
                />
                <squadbox-assistant-builder-trait-compliance-form
                  #complianceFormFr
                  [class.hidden]="!supportMultilingual()"
                  [isMultilingual]="true"
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                  [configurationUploadStore]="complianceConfigStore"
                  [referenceUploadStore]="complianceReferenceStore"
                  language="fr"
                  [secondaryForm]="complianceFormEn.form()"
                  secondaryLanguage="en"
                />
                <squadbox-assistant-builder-trait-compliance-form
                  #complianceFormEn
                  class="hidden"
                  [renderCustomizeText]="false"
                  [isMultilingual]="true"
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                  [configurationUploadStore]="complianceConfigStore"
                  [referenceUploadStore]="complianceReferenceStore"
                  language="en"
                />
              }
              @case ('creative') {
                <squadbox-assistant-builder-trait-creative-form
                  #traitForm
                  [fileUploadTransactionId]="fileUploadTransactionId()"
                />
              }
            }
          </div>
        </div>

        <!-- Share panel (right on desktop, gradient surface — Figma 25307:22705) -->
        @if (isShareSectionVisible()) {
          <div
            class="
                p-6
                border-t border-neutral-200
                bg-gradient-to-b from-neutral-50 to-neutral-100
                tablet-landscape:border-t-0 tablet-landscape:border-l
                tablet-landscape:py-8 tablet-landscape:w-100
                tablet-landscape:overflow-y-auto hide-scrollbar
              "
          >
            <squadbox-assistant-share-section
              [allUsers]="allUsers()"
              [allGroups]="allGroups()"
              [currentUserId]="userId()?.id ?? ''"
              [isOrgWideEnabled]="ffShareAssistant().everyoneInMyOrganization"
              [(shareOption)]="shareOption"
              [(selectedUserIds)]="shareSelectedUserIds"
              [(selectedGroupIds)]="shareSelectedGroupIds"
            />
          </div>
        }
      </div>
    </sd-modal>

    <!-- Confirm delete (edit only) — mirrors the assistants-list confirm modal. -->
    <sd-modal
      [isModalVisible]="isConfirmDeleteModalOpen()"
      [modalOptions]="{
        title: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.TITLE' | translate,
        showCloseButton: false,
        actions: [
          {
            label: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.CTA_CANCEL' | translate,
            action: 'cancel',
            type: 'cancel',
            disabled: false,
          },
          {
            label: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.CTA_DELETE' | translate,
            action: 'delete',
            type: 'delete',
            disabled: false,
          },
        ],
        backdropDismiss: true,
      }"
      (triggerAction)="handleConfirmDeleteModalAction($event)"
    >
      <div
        class="w-auto tablet-portrait:w-120"
        innerHTML="{{
          'ASSISTANTS.DELETE_ASSISTANT_MODAL.DESCRIPTION'
            | translate: { assistantName: assistant()?.name }
        }}"
      ></div>
    </sd-modal>
  `,
})
export class AssistantBuilderPage implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private readonly translateStore = inject(TranslateStore);
  private readonly assistantsService = inject(fromGenerated.AssistantsService);
  private readonly assistantsStore = inject(Store<AssistantsState>);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly injector = inject(Injector);
  private readonly usersService = inject(fromGenerated.UsersService);
  private readonly groupsService = inject(fromGenerated.GroupsService);
  private readonly featureFlagsStore = inject(
    Store<fromFeatureFlags.FeatureFlagsState>,
  );
  /**
   * The skill being edited, or `undefined` when creating. Fed by the assistants
   * page from the loaded entities (SB-3325 PR-4, Stage B) — the builder is a
   * modal rendered over the list, not a routed page, so there is no resolver.
   */
  public readonly assistant = input<fromGenerated.AssistantView | undefined>(
    undefined,
  );

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly userId = this.authStore.selectSignal(
    fromAuth.authFeature.selectUser,
  );
  /**
   * Page-owned compliance shared stores (SB-3325 PR-5): one per LOGICAL upload
   * zone (config skill.md + reference docs), bound to all three und/fr/en slots
   * so chips, upload state, and validator errors are identical and gate Save
   * cross-slot. Created imperatively — never a DI provider (lifecycle decision
   * #2: a bare `inject(FileUploadV2Store)` in the builder tree throws by
   * construction). The factory ties teardown to the page DestroyRef; the trait
   * switch + destroy effects below reset them (lifecycle decision #3).
   */
  public readonly complianceConfigStore: FileUploadV2Store =
    createFileUploadV2Store(this.injector);
  public readonly complianceReferenceStore: FileUploadV2Store =
    createFileUploadV2Store(this.injector);
  private readonly favoriteEntities = this.assistantsStore.selectSignal(
    fromFavorites.assistantFavoritesFeature.selectEntities,
  );
  /**
   * Staged favorite for the New (create) state (D6 / spec §5): the star is
   * stageable before the skill exists and applied once it is created; discarded
   * on cancel (the component is destroyed when the modal closes). On edit the
   * favorite commits instantly via the favorites store.
   */
  public readonly stagedFavorite = signal<boolean>(false);
  /** Whether the skill is favorited: the store on edit, the staged flag on create. */
  public readonly isFavorite = computed(() => {
    const skill = this.assistant();
    if (!skill) return this.stagedFavorite();
    return !!this.favoriteEntities()?.[skill.uuid];
  });
  /**
   * Toggle the favorite. On edit it commits instantly (favorites store); on
   * create it only stages the choice, applied on successful create.
   */
  public handleToggleFavorite(): void {
    const skill = this.assistant();
    if (!skill) {
      this.stagedFavorite.update((staged) => !staged);
      return;
    }
    this.assistantsStore.dispatch(
      this.isFavorite()
        ? fromFavorites.AssistantFavoritesActions.removeFavorite({
            assistantId: skill.uuid,
          })
        : fromFavorites.AssistantFavoritesActions.addFavorite({
            assistantId: skill.uuid,
          }),
    );
  }

  public readonly fileUploadTransactionId = signal<string>('');
  public readonly selectedType = signal<TypeOption>(TypeOptions.insight);
  public readonly isSaving = signal(false);
  /**
   * The assistant uuid whose forms have already been seeded, so the assistant-
   * load effect patches once per load and never re-patches over user edits.
   */
  private patchedAssistantUuid: string | null = null;

  // Generation-model selection (SB-3310). The catalog is loaded from the
  // catalog endpoint (SB-3318, see loadModelCatalog); `selectedModelSlug` holds
  // the chosen slug and is fed into the create payload. Empty until the catalog
  // resolves — null-safe (no slug sent → deployment default).
  public readonly availableModels = signal<ModelOption[]>([]);
  public readonly selectedModelSlug = signal<string>('');

  // Share panel signals
  public readonly allUsers = signal<fromGenerated.UserSummaryView[]>([]);
  public readonly allGroups = signal<fromGenerated.GroupSummaryView[]>([]);
  // Default "Only people invited" (owner-only until people are invited) — KD-23.
  public readonly shareOption = signal<ShareVisibility>('shared');
  public readonly shareSelectedUserIds = signal<string[]>([]);
  public readonly shareSelectedGroupIds = signal<string[]>([]);
  // Guards the replace-semantics reconcile: false until current access is read
  // (edit) or there is nothing to read (new). See seedShareState.
  public readonly shareAccessLoaded = signal<boolean>(false);
  public readonly ffShareAssistant = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectShareAssistant,
  );
  /**
   * KD-6 (revised): the kept UI-only flag, re-pointed from forking the
   * create/edit surface to gating Type + Model visibility in this unified
   * builder. `selectFeatureFlag` yields `undefined` (falsy) until LaunchDarkly
   * resolves, so create fails closed to the insight-only basic experience.
   * There is deliberately NO server-side enforcement of this gate (KD-6/R5).
   */
  public readonly ffAdvancedAssistantBuilder =
    this.featureFlagsStore.selectSignal(
      fromFeatureFlags.featureFlagsFeature.selectFeatureFlag(
        'ff-advanced-assistant-builder',
      ),
    );
  // One-shot guard for the reactive picker load: the share flag defaults to
  // disabled until LaunchDarkly resolves, so we load when it first turns on and
  // never refetch.
  private shareTargetsLoaded = false;

  /**
   * Per-locale stored (original) description, or `null` when the locale has no
   * baseline (create, or a locale added during this edit). Keyed on the active
   * locale exactly like the DTO the update builder sends, so the grandfather
   * comparison matches the server's stored-value lookup. Single source for both
   * the display ceiling below and the basic form's grandfather validator.
   */
  private storedDescriptionFor(locale: BuilderLocale): string | null {
    const stored = this.assistant()?.translations ?? [];
    // The single-language (`und`) slot grandfathers the skill's one stored
    // translation regardless of the locale it was saved under (und/fr/en). A
    // multilingual skill (>1 stored) has no single baseline for `und`, so it is
    // treated as create-strict when authored fresh in the OFF slot.
    if (locale === 'und') {
      return stored.length === 1 ? (stored[0]?.description ?? null) : null;
    }
    return stored.find((t) => t.locale === locale)?.description ?? null;
  }

  /**
   * Per-locale effective description display ceiling (T5/KD-7, FM-3): the
   * create-strict cap, raised to the stored description's length on edit so a
   * pristine grandfathered value fits its counter, hard-capped by the update
   * maximum. Feeds the field counter / `maxlength` only; the create-strict cap
   * on edited values is validated by `grandfatheredDescriptionMaxLength` keyed
   * on the stored value below. Local function by design: PR-1's final contract
   * ships no shared effective-limit helper.
   */
  private descriptionMaxFor(locale: BuilderLocale): number {
    const stored = this.storedDescriptionFor(locale);
    return Math.min(
      Math.max(DESCRIPTION_MAX_LENGTH, stored?.length ?? 0),
      DESCRIPTION_UPDATE_MAX_LENGTH,
    );
  }

  /**
   * The active UI language, tracked for the single-language (und) compliance
   * slot so its "Customize the upload text" placeholders/defaults resolve in the
   * UI's language rather than a hard-coded locale (KD-21).
   */
  public readonly currentUiLanguage = signal<SupportedLanguage>('en');

  /**
   * SB-3325 PR-4: opt-in multilingual authoring. OFF → one universal locale
   * (persisted as `und` for new skills, or the skill's single stored locale on
   * edit — never rewritten, KD-C); ON → distinct fr + en, both required. Seeded
   * on edit from the stored translation count.
   */
  public readonly supportMultilingual = signal<boolean>(false);
  /**
   * Active authoring slots (KD-18 / spec §6): the single `und` slot when the
   * toggle is OFF, or both fr + en when ON. Only the active slot(s) render,
   * validate, and persist. The three slots are independent FormGroups, so
   * toggling carries nothing between the single-language and multilingual modes.
   */
  public readonly activeLocales = computed<BuilderLocale[]>(() =>
    this.supportMultilingual() ? ['fr', 'en'] : ['und'],
  );

  public readonly descriptionMaxLengthByLocale = computed(() => ({
    und: this.descriptionMaxFor('und'),
    fr: this.descriptionMaxFor('fr'),
    en: this.descriptionMaxFor('en'),
  }));
  public readonly descriptionStoredValueByLocale = computed(() => ({
    und: this.storedDescriptionFor('und'),
    fr: this.storedDescriptionFor('fr'),
    en: this.storedDescriptionFor('en'),
  }));

  // Single locale-aware basic form ref (renders per-locale rows internally).
  public readonly basicFormComponent =
    viewChild<AssistantBuilderBasicFormComponent>('basicForm');

  // Trait form refs (insight/comparison/creative: single form; compliance: per-slot)
  public readonly traitFormComponent =
    viewChild<AssistantBuilderTraitForm>('traitForm');
  public readonly complianceFormUndComponent =
    viewChild<AssistantBuilderTraitComplianceFormComponent>(
      'complianceFormUnd',
    );
  public readonly complianceFormFrComponent =
    viewChild<AssistantBuilderTraitComplianceFormComponent>('complianceFormFr');
  public readonly complianceFormEnComponent =
    viewChild<AssistantBuilderTraitComplianceFormComponent>('complianceFormEn');

  /**
   * The compliance form instance for a builder slot (KD-21 three-slot): `und`
   * is the single-language (OFF) editing surface; `fr`/`en` are the
   * multilingual slots. The three are independent so labels never carry.
   */
  private complianceFormFor(
    locale: BuilderLocale,
  ): AssistantBuilderTraitComplianceFormComponent | undefined {
    switch (locale) {
      case 'en':
        return this.complianceFormEnComponent();
      case 'fr':
        return this.complianceFormFrComponent();
      default:
        return this.complianceFormUndComponent();
    }
  }

  /**
   * Incremented whenever any relevant form's statusChanges emits.
   * Bridges RxJS form observables to signal reactivity for formValidity and
   * invalidLocales computed signals.
   */
  private readonly formStatusVersion = signal(0);

  /**
   * Whether the form can be saved: the selected locale's basic form, every
   * OTHER populated locale's basic form (a locale is populated when it has a
   * non-empty name or description — otherwise its invalid-but-empty state is
   * fine, the DTO simply omits it), and the trait form must all be valid.
   * Gating populated locales closes the silent partial save (A1 Attack-3):
   * previously an invalid EN tab was silently dropped from the payload.
   */
  protected readonly formValidity = computed(() => {
    this.formStatusVersion();

    const basicForm = this.basicFormComponent();
    const active = this.activeLocales();
    // Every active locale's basic form must be valid (OFF → the one primary
    // locale; ON → both fr + en, both required per KD-B). Inactive-locale
    // groups are disabled inside the component, so they never gate Save.
    const basicValid = active.every(
      (locale) => basicForm?.form(locale).valid ?? false,
    );

    let traitValid: boolean;
    // Compare by trait discriminant, not object identity: selectedType() may be
    // set to any TypeOption-shaped object, so `=== TypeOptions.x` is fragile.
    const type = this.selectedType().type;
    if (type === TypeOptions.compliance.type) {
      // Each active slot's compliance form must be valid (OFF → und; ON → fr+en).
      traitValid = active.every(
        (locale) => this.complianceFormFor(locale)?.form().valid ?? false,
      );
    } else if (
      type === TypeOptions.insight.type ||
      type === TypeOptions.generic.type ||
      type === TypeOptions.comparison.type ||
      type === TypeOptions.creative.type
    ) {
      const traitForm = this.traitFormComponent()?.form();
      traitValid = traitForm?.valid ?? false;
    } else {
      traitValid = true;
    }

    return basicValid && traitValid;
  });

  /**
   * Whether the share panel is shown: gated on the share feature flag. The
   * builder is only ever reachable for skills the user owns or is creating
   * (SB-3325 PR-6 removed the read-only, non-owner entry points), so no
   * ownership gate is needed here.
   */
  protected readonly isShareSectionVisible = computed(
    () => this.ffShareAssistant().enabled,
  );

  ngOnInit() {
    this.fileUploadTransactionId.set(uuidv4());
    this.loadModelCatalog();
  }

  /**
   * Load the deployment generation-model catalog for the Model dropdown,
   * mirroring loadShareableTargets(). A failure is captured (not silent) and
   * leaves the catalog empty rather than erroring — null-safe: the dropdown
   * shows no options and create omits `model` (→ deployment default). UI
   * enablement is gated on catalog provisioning (ticket Pre-flight) so users
   * don't see an empty control before the chain is live.
   */
  private loadModelCatalog(): void {
    this.assistantsService
      .assistantsControllerGetAvailableModelsV1()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        sentryCaptureError,
        catchError(() => EMPTY),
      )
      .subscribe((models) => {
        // Map AvailableModelView → ModelOption at this boundary so the component
        // stays decoupled from the generated client (do not feed the generated
        // type straight in — see ModelOption).
        this.availableModels.set(
          models.map((model) => ({
            slug: model.slug,
            displayName: model.displayName,
            isDefault: model.isDefault,
          })),
        );
        this.applyModelSelection();
      });
  }

  /**
   * Pre-select the model: on edit, the skill's persisted slug; otherwise (create,
   * or a pre-epic skill whose stored `model` is null) the catalog default. A
   * stored slug no longer in the catalog still shows read-only (the selector
   * falls back to the raw slug).
   *
   * Keep this resolution HERE (run from the catalog `.subscribe` callback, where
   * `assistant()` is already set). Do NOT move it into the `assistant()` form
   * effect in the constructor — that effect can fire before the catalog
   * resolves, so the stored-slug pre-select would run against an empty catalog.
   */
  private applyModelSelection(): void {
    const defaultSlug =
      this.availableModels().find((option) => option.isDefault)?.slug ?? '';
    const storedSlug = this.assistant()?.model ?? null;
    this.selectedModelSlug.set(storedSlug ?? defaultSlug);
  }

  constructor() {
    // Track the active UI language for the single-language (und) compliance
    // slot's per-language customize defaults (KD-21).
    this.currentUiLanguage.set(
      (this.translateService.getCurrentLang() ??
        this.translateService.getFallbackLang() ??
        'en') as SupportedLanguage,
    );
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) =>
        this.currentUiLanguage.set(event.lang as SupportedLanguage),
      );

    // Load the people/group pickers the first time the share flag turns on.
    // The flag defaults to disabled until LaunchDarkly resolves, so loading
    // only in `ngOnInit` would miss a late-arriving enable and leave the panel
    // with empty pickers; the guard prevents refetching once populated.
    effect(() => {
      if (this.ffShareAssistant().enabled && !this.shareTargetsLoaded) {
        this.shareTargetsLoaded = true;
        this.loadShareableTargets();
      }
    });

    /**
     * Load assistant into forms when assistant input is set.
     */
    effect(() => {
      const assistant = this.assistant();
      if (!assistant) return;

      this.selectedType.set(
        TypeOptions[
          assistant.trait as
            | 'insight'
            | 'generic'
            | 'comparison'
            | 'compliance'
            | 'creative'
        ],
      );

      // Multilingual is ON only when the skill has a real fr + en pair (KD-A).
      // Content-based, not count-based: legacy/partial shapes like [und,fr],
      // [und,en], or [und,fr,en] open OFF (Decision C stale-translation fix) so
      // the rows seed by their actual locale instead of colliding.
      this.supportMultilingual.set(this.hasFrAndEn(assistant));

      // Seed the forms exactly ONCE per loaded assistant, after the relevant
      // view-child form refs resolve. This effect legitimately re-runs (the refs
      // resolve across passes), but it also re-fires after the user edits a
      // form — and re-running the patch would clobber those edits. The visible
      // symptom was edit-mode document removal silently re-seeding: removeFile
      // clears the store, this effect re-patches the trait form from the
      // unchanged assistant, and the file-upload CVA's writeValue re-inserts the
      // document. The selectedType/supportMultilingual sets above are idempotent,
      // so leaving them outside the guard is harmless.
      if (this.patchedAssistantUuid === assistant.uuid) return;
      const traitFormReady =
        assistant.trait === TypeOptions.compliance.type
          ? !!this.complianceFormUndComponent()
          : !!this.traitFormComponent();
      if (!this.basicFormComponent() || !traitFormReady) return;
      this.patchedAssistantUuid = assistant.uuid;

      // Patch the locale-aware basic form
      this.patchBasicFormsFromAssistant(assistant);

      // Patch trait form
      this.patchTraitFormFromAssistant(assistant);
    });

    /**
     * Seed the share panel from the skill's current access. Kept in its own
     * effect (not the form-patch one) so resolving `userId` / the share flag
     * only re-reads access — it never re-patches the forms. `onCleanup` cancels
     * an in-flight read before a re-run starts a new one, so a slow response
     * can't land out of order over a newer assistant's access.
     */
    effect((onCleanup) => {
      const sub = this.seedShareState(this.assistant());
      onCleanup(() => sub?.unsubscribe());
    });

    /**
     * Keep shared compliance fields in sync across the und/fr/en slots.
     * Non-translatable fields (removePromptInput, userWillUploadReferenceDocuments,
     * configurationFile, files) are bidirectional so files/toggles/skill.md carry
     * when toggling multilingual; the per-locale customize labels (referenceLabel,
     * regularLabel) are excluded and never carry (KD-21 three-slot no-carry).
     */
    effect((onCleanup) => {
      if (this.selectedType().type !== TypeOptions.compliance.type) return;

      const forms = (['und', 'fr', 'en'] as const)
        .map((locale) => this.complianceFormFor(locale)?.form())
        .filter((form): form is NonNullable<typeof form> => !!form);
      if (forms.length < 2) return;

      const syncFrom = (source: (typeof forms)[number]) => {
        const patch = Object.fromEntries(
          COMPLIANCE_NOT_TRANSLATED_FORM_KEYS.map((formControlKey) => [
            formControlKey,
            source.get(formControlKey)?.value,
          ]),
        );
        for (const target of forms) {
          if (target === source) continue;
          const differs = COMPLIANCE_NOT_TRANSLATED_FORM_KEYS.some(
            (formControlKey) =>
              target.get(formControlKey)?.value !== patch[formControlKey],
          );
          if (differs) {
            const toggleChanged =
              target.controls.userWillUploadReferenceDocuments.value !==
              patch['userWillUploadReferenceDocuments'];
            target.patchValue(patch, { emitEvent: false });
            // SBB-16: each slot component tracks this toggle via valueChanges
            // (toSignal) to disable its reference-label field — a silent mirror
            // would leave the newly-synced slot's disable state stale. Re-emit
            // only the toggle; the differs-guard above stops any sync loop.
            if (toggleChanged) {
              target.controls.userWillUploadReferenceDocuments.setValue(
                patch['userWillUploadReferenceDocuments'] as boolean,
              );
            }
          }
        }
      };

      // Build the subscriptions with loops (not nested map/flatMap) to keep the
      // callback nesting shallow.
      const subs: (Subscription | undefined)[] = [];
      for (const form of forms) {
        for (const formControlKey of COMPLIANCE_NOT_TRANSLATED_FORM_KEYS) {
          subs.push(
            form
              .get(formControlKey)
              ?.valueChanges?.subscribe(() => syncFrom(form)),
          );
        }
      }
      onCleanup(() => subs.forEach((sub) => sub?.unsubscribe()));
    });

    /**
     * Bridge form statusChanges to formStatusVersion signal.
     * Subscribes only to forms that exist for the current selectedType
     * (compliance forms vs trait form).
     */
    effect((onCleanup) => {
      this.activeLocales();
      this.selectedType();

      const basicForm = this.basicFormComponent();
      const undForm = basicForm?.form('und');
      const frForm = basicForm?.form('fr');
      const enForm = basicForm?.form('en');
      const traitForm = this.traitFormComponent()?.form();

      const bump = () => this.formStatusVersion.update((v) => v + 1);
      bump(); // initial

      // Subscribe to all three basic slots (und/fr/en). The `und` slot is the
      // OFF-mode editing surface, so its status must bump the version too or
      // formValidity would go stale when the single-language form changes.
      const subs = [
        undForm?.statusChanges.subscribe(bump),
        frForm?.statusChanges.subscribe(bump),
        enForm?.statusChanges.subscribe(bump),
      ];

      if (this.selectedType().type === TypeOptions.compliance.type) {
        // All three compliance slots (und/fr/en) so validity tracks whichever
        // mode is active.
        subs.push(
          this.complianceFormUndComponent()
            ?.form()
            .statusChanges.subscribe(bump),
          this.complianceFormFrComponent()
            ?.form()
            .statusChanges.subscribe(bump),
          this.complianceFormEnComponent()
            ?.form()
            .statusChanges.subscribe(bump),
        );
      } else {
        subs.push(traitForm?.statusChanges.subscribe(bump));
      }

      onCleanup(() => subs.forEach((s) => s?.unsubscribe()));
    });

    // Compliance shared-store lifecycle (SB-3325 PR-5, lifecycle decision #3):
    // reset both page-owned stores when the trait LEAVES compliance so a
    // compliance → other → compliance round-trip re-enters empty-until-seeded.
    // Entering compliance needs no reset (the stores are already fresh). The
    // multilingual toggle deliberately does NOT reset them (closed-loop
    // invariant #4 — chips survive OFF↔ON).
    let wasCompliance = this.selectedType().type === 'compliance';
    effect(() => {
      const isCompliance = this.selectedType().type === 'compliance';
      if (wasCompliance && !isCompliance) {
        this.complianceConfigStore.reset();
        this.complianceReferenceStore.reset();
      }
      wasCompliance = isCompliance;
    });

    // Belt-and-suspenders on top of the factory's DestroyRef teardown.
    this.destroyRef.onDestroy(() => {
      this.complianceConfigStore.reset();
      this.complianceReferenceStore.reset();
    });
  }

  /**
   * Whether the skill has a real fr + en translation pair — the only shape that
   * opens the multilingual toggle ON (Decision C). Count-based inference would
   * force stale shapes ([und,fr], [und,en], [und,fr,en]) ON and collide rows.
   */
  private hasFrAndEn(assistant: fromGenerated.AssistantView): boolean {
    const locales = new Set(
      (assistant.translations ?? []).map((t) => t.locale),
    );
    return locales.has('fr') && locales.has('en');
  }

  /**
   * The single translation to seed the OFF (und) slot from, by priority
   * und > fr > en (und is canonical; then legacy fr, then en), else the first
   * row. Used for both the basic slot and the compliance labels in OFF mode.
   */
  private pickOffSource(
    translations: fromGenerated.AssistantTranslationView[],
  ): fromGenerated.AssistantTranslationView | undefined {
    return (
      translations.find((t) => t.locale === 'und') ??
      translations.find((t) => t.locale === 'fr') ??
      translations.find((t) => t.locale === 'en') ??
      translations[0]
    );
  }

  /** Patches the locale-aware basic form from assistant translations. */
  private patchBasicFormsFromAssistant(assistant: fromGenerated.AssistantView) {
    const basicForm = this.basicFormComponent();
    if (!basicForm) return;

    // Logo is a single shared control (not per-locale).
    basicForm.logoControl.setValue(assistant.logo, { emitEvent: true });

    // Seed slots by ACTUAL stored locale (Decision C). A real fr+en pair opens
    // ON and seeds the fr/en slots from their own rows (any stray und is
    // ignored). Otherwise the skill opens OFF and seeds the single `und` slot
    // from one deterministic source (und > fr > en); its stored locale is
    // preserved on save via persistLocaleFor.
    const patchSlot = (
      locale: BuilderLocale,
      translation: fromGenerated.AssistantTranslationView | undefined,
    ) => {
      if (!translation) return;
      basicForm.form(locale).patchValue(
        {
          name: translation.name ?? assistant.name,
          description: translation.description ?? assistant.description,
          ...(translation.introMessage
            ? { introMessage: translation.introMessage }
            : {}),
        },
        { emitEvent: true },
      );
    };

    if (this.hasFrAndEn(assistant)) {
      patchSlot(
        'fr',
        assistant.translations.find((t) => t.locale === 'fr'),
      );
      patchSlot(
        'en',
        assistant.translations.find((t) => t.locale === 'en'),
      );
    } else {
      patchSlot('und', this.pickOffSource(assistant.translations));
    }
  }

  /** Maps non-deleted assistant documents to trait form file chip values. */
  private fileChipsFromAssistantDocuments(
    assistant: fromGenerated.AssistantView,
  ): AssistantBuilderFileValue[] {
    return (
      assistant.documents
        ?.filter((doc) => !doc.deletedAt)
        .map((doc) => ({
          id: doc.uuid,
          name: doc.filename,
          extension: doc.extension,
          size: doc.size,
        })) ?? []
    );
  }

  /**
   * uuids of the assistant's live documents carrying the `instruction` role —
   * the skill.md (PR-2 stamps/normalizes it server-side). Drives the
   * role-first edit-mode split between the configuration-file control and the
   * knowledge/reference controls.
   */
  private instructionDocumentUuids(
    assistant: fromGenerated.AssistantView,
  ): Set<string> {
    return new Set(
      (assistant.documents ?? [])
        .filter((doc) => !doc.deletedAt && doc.role === 'instruction')
        .map((doc) => doc.uuid),
    );
  }

  private patchComplianceTraitFormsFromAssistant(
    assistant: fromGenerated.AssistantView,
    files: AssistantBuilderFileValue[],
  ) {
    const traitConfig = assistant.traitConfig;
    if (!isComplianceConfig(traitConfig)) return;

    const undForm = this.complianceFormUndComponent()?.form();
    const frForm = this.complianceFormFrComponent()?.form();
    const enForm = this.complianceFormEnComponent()?.form();
    if (!undForm || !frForm || !enForm) return;

    // Role-first split: instruction → configurationFile, rest → files.
    // Compliance skills exist only new-model (legacy are archived by T8), so
    // the role is always present — no filename fallback needed here.
    const instructionUuids = this.instructionDocumentUuids(assistant);
    const sharedPatch = {
      removePromptInput: traitConfig.removePromptInput,
      userWillUploadReferenceDocuments:
        traitConfig.userWillUploadReferenceDocuments,
      configurationFile: files.filter((file) => instructionUuids.has(file.id)),
      files: files.filter((file) => !instructionUuids.has(file.id)),
    };

    // Non-translatable fields are shared → seed all three slots so they carry
    // when the multilingual toggle flips (the three-way sync only fires on later
    // value changes, not on this initial patch).
    undForm.patchValue(sharedPatch, { emitEvent: false });
    frForm.patchValue(sharedPatch, { emitEvent: false });
    enForm.patchValue(sharedPatch, { emitEvent: false });

    // SBB-16: each slot component tracks userWillUploadReferenceDocuments via
    // valueChanges (toSignal) to disable its reference-label field — the silent
    // seed above never reaches it, so an edited skill saved with the toggle ON
    // would open with the field stuck disabled. Re-emit just that control; the
    // three-way sync's differs-guard makes this a no-op mirror-wise.
    for (const form of [undForm, frForm, enForm]) {
      form.controls.userWillUploadReferenceDocuments.setValue(
        sharedPatch.userWillUploadReferenceDocuments,
      );
    }

    // Customize labels seed ONLY the active mode's slot(s) — no carry into the
    // inactive mode (KD-21 three-slot, mirrors patchBasicFormsFromAssistant).
    // Content-based gate (Decision C) so [und,fr]/[und,en]/[und,fr,en] open OFF
    // and seed the und slot from the deterministic OFF source (und > fr > en).
    const labelsFrom = (
      translation: fromGenerated.AssistantTranslationView | undefined,
    ) => {
      const tct =
        translation &&
        isComplianceConfigTranslation(translation.traitConfigTranslation)
          ? translation.traitConfigTranslation
          : undefined;
      return {
        referenceLabel: tct?.labelForExpectedDocuments.referenceLabel ?? '',
        regularLabel: tct?.labelForExpectedDocuments.regularLabel ?? '',
      };
    };

    if (this.hasFrAndEn(assistant)) {
      frForm.patchValue(
        labelsFrom(assistant.translations.find((t) => t.locale === 'fr')),
        { emitEvent: false },
      );
      enForm.patchValue(
        labelsFrom(assistant.translations.find((t) => t.locale === 'en')),
        { emitEvent: false },
      );
    } else {
      undForm.patchValue(
        labelsFrom(this.pickOffSource(assistant.translations)),
        { emitEvent: false },
      );
    }
  }

  /** Patches trait form(s) from assistant; compliance gets per-locale patches. */
  private patchTraitFormFromAssistant(assistant: fromGenerated.AssistantView) {
    const files = this.fileChipsFromAssistantDocuments(assistant);
    const traitForm = this.traitFormComponent()?.form();
    const instructionUuids = this.instructionDocumentUuids(assistant);

    switch (assistant.trait) {
      case 'insight':
        traitForm?.patchValue(
          { prompt: assistant.prompt, files },
          { emitEvent: false },
        );
        break;
      case 'generic': {
        // Role-first split, with the exact-filename match kept as a LEGACY
        // fallback: pre-existing generic rows keep role `regular` until
        // PR-2's backfill normalizes them (KD-15 extended).
        const isConfigurationFile = (file: { id: string; name: string }) =>
          instructionUuids.has(file.id) ||
          file.name === GENERIC_ASSISTANT_CONFIGURATION_FILENAME;
        traitForm?.patchValue(
          {
            sourceFiles: files.filter((file) => !isConfigurationFile(file)),
            configurationFile: files.filter(isConfigurationFile),
          },
          { emitEvent: false },
        );
        break;
      }
      case 'comparison':
        // Dedicated case (T4): behavioral toggle from traitConfig + the
        // skill.md by role — no more `default` fallthrough spreading the
        // legacy free-text config into removed controls.
        traitForm?.patchValue(
          {
            removePromptInput: hasRemovePromptInput(assistant.traitConfig),
            configurationFile: files.filter((file) =>
              instructionUuids.has(file.id),
            ),
          },
          { emitEvent: false },
        );
        break;
      case 'compliance':
        this.patchComplianceTraitFormsFromAssistant(assistant, files);
        break;
      case 'creative':
        traitForm?.patchValue({ files }, { emitEvent: false });
        break;
      default:
        traitForm?.patchValue(
          { ...assistant.traitConfig },
          { emitEvent: false },
        );
    }
  }

  /**
   * Single source of truth for disabling Save and for bailing out of the save handler.
   */
  public isSaveButtonDisabled(): boolean {
    // Upload state now gates Save through ordinary form validity: each zone
    // registers NG_VALIDATORS ({uploadPending}/{uploadFailed}/{uploadInvalid})
    // on its control, so a pending/failed/invalid upload invalidates the trait
    // form cross-slot (SB-3325 PR-5). The old global `selectAreFilesLoading`
    // gate is gone.
    return !this.formValidity() || this.isSaving();
  }

  /**
   * Defense-in-depth for the compliance shared-store path (SB-3325 PR-5): the
   * disabled button + form validity are the primary gate, but re-check the two
   * page-owned stores directly so a save fails loud rather than shipping an
   * in-flight/invalid/failed file (never a silent done-only DTO filter, which
   * would drop the file with no signal).
   */
  private hasBlockingComplianceUpload(): boolean {
    if (this.selectedType().type !== TypeOptions.compliance.type) return false;
    const isBlocking = (store: FileUploadV2Store): boolean =>
      store
        .getFiles()
        .some(
          (file) =>
            file.status === 'upload-pending' ||
            file.status === 'upload-ongoing' ||
            file.status === 'upload-invalid' ||
            file.status === 'upload-failed',
        );
    return (
      isBlocking(this.complianceConfigStore) ||
      isBlocking(this.complianceReferenceStore)
    );
  }

  public async handleClickSaveCTA() {
    if (this.isSaveButtonDisabled() || this.hasBlockingComplianceUpload()) {
      return;
    }
    this.isSaving.set(true);
    try {
      const assistant = this.assistant();

      let skillUuid: string;
      if (assistant) {
        await firstValueFrom(
          this.assistantsService.assistantsControllerUpdateAssistantV1(
            assistant.uuid,
            this.buildUpdateAssistantDto(assistant),
          ),
        );
        skillUuid = assistant.uuid;
      } else {
        const created = await firstValueFrom(
          this.assistantsService.assistantsControllerCreateAssistantV1(
            this.buildCreateAssistantDto(),
          ),
        );
        skillUuid = created.uuid;
        // Apply the favorite staged in the New state (D6 / spec §5).
        if (this.stagedFavorite()) {
          this.assistantsStore.dispatch(
            fromFavorites.AssistantFavoritesActions.addFavorite({
              assistantId: created.uuid,
            }),
          );
        }
      }
      // Reconcile sharing after the skill is persisted (needs its uuid). A
      // sharing failure is surfaced but must not fail the (saved) skill.
      await this.reconcileShareAccess(skillUuid);
      this.assistantsStore.dispatch(AssistantsActions.loadAssistants());
      // Create and edit both close the modal on save (mirrors the teammate
      // builder); the list reloads above.
      this.closeBuilder();
      // eslint-disable-next-line sonarjs/no-ignored-exceptions
    } catch (_error) {
      this.toastrService.error(
        this.translateService.instant(
          'ASSISTANTS.ASSISTANT_BUILDER.CTA_SAVE_ERROR',
        ) as string,
        undefined,
        {
          closeButton: true,
          disableTimeOut: true,
        },
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * sd-modal footer config (SB-3325 PR-4, Stage B). No chrome title/close — the
   * builder renders its own in-body header (title + favorite star), so the
   * dialog's accessible name is supplied via `ariaLabel` (the skill name, or the
   * create title). Delete (edit only) is pulled to the far left via
   * separateDestructiveAction, Cancel + Save/Create sit on the right. Capped at
   * 1512 px via `maxWidth` so the surface stays centered with a horizontal
   * gutter below the cap (never full-bleed), 80 px margins (D1).
   */
  protected modalOptions(): fromSeriousSystem.Modals {
    const actions: fromSeriousSystem.ModalAction[] = [];
    if (this.assistant()) {
      actions.push({
        label: this.translateService.instant(
          'ASSISTANTS.DELETE_ASSISTANT_MODAL.CTA_DELETE',
        ) as string,
        action: 'delete',
        type: 'delete-text',
        leadingIcon: { type: 'uiBasic', icon: 'delete' },
        disabled: this.isSaving(),
      });
    }
    actions.push({
      label: this.translateService.instant(
        'ASSISTANTS.ASSISTANT_BUILDER.CTA_CANCEL',
      ) as string,
      action: 'cancel',
      type: 'cancel',
      disabled: this.isSaving(),
    });
    actions.push({
      label: this.translateService.instant(
        this.assistant()
          ? 'ASSISTANTS.ASSISTANT_BUILDER.CTA_SAVE'
          : 'ASSISTANTS.ASSISTANT_BUILDER.CTA_CREATE',
      ) as string,
      action: 'save',
      type: 'save',
      disabled: this.isSaveButtonDisabled(),
    });
    return {
      title: '',
      ariaLabel:
        this.assistant()?.name ??
        (this.translateService.instant(
          'ASSISTANTS.ASSISTANT_BUILDER.TITLE_CREATE',
        ) as string),
      maxWidth: 1512,
      showCloseButton: false,
      showFooterSeparator: true,
      backdropDismiss: !this.isSaving(),
      separateDestructiveAction: true,
      actions,
    };
  }

  /** sd-modal action router: save / delete / (cancel|backdrop|escape → close). */
  protected onModalAction(action: string): void {
    switch (action) {
      case 'save':
        void this.handleClickSaveCTA();
        break;
      case 'delete':
        this.handleClickDeleteCTA();
        break;
      default:
        this.handleClickCancelCTA();
    }
  }

  /** Close the builder modal (dispatches the store close action). */
  private closeBuilder(): void {
    this.assistantsStore.dispatch(AssistantBuilderModalActions.close());
  }

  /**
   * Whether the builder holds unsaved authoring work — drives the discard
   * confirm on cancel/backdrop/escape. Keys on the reactive forms' dirty state:
   * loading an existing skill patches without marking dirty, so only genuine
   * user edits count.
   */
  public hasChanges(): boolean {
    const basicForm = this.basicFormComponent();
    const basicDirty = this.activeLocales().some(
      (locale) => basicForm?.form(locale).dirty ?? false,
    );
    const logoDirty = basicForm?.logoControl.dirty ?? false;
    const traitDirty =
      this.selectedType().type === TypeOptions.compliance.type
        ? !!this.complianceFormUndComponent()?.form().dirty ||
          !!this.complianceFormFrComponent()?.form().dirty ||
          !!this.complianceFormEnComponent()?.form().dirty
        : !!this.traitFormComponent()?.form().dirty;
    return basicDirty || logoDirty || traitDirty;
  }

  public handleClickCancelCTA() {
    if (!this.hasChanges()) {
      this.closeBuilder();
      return;
    }
    this.confirmDialog
      .confirm({
        title: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.TITLE',
        ) as string,
        message: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.DESCRIPTION',
        ) as string,
        confirmLabel: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.CTA_DISCARD',
        ) as string,
        cancelLabel: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.CTA_CANCEL',
        ) as string,
        confirmType: 'delete',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.closeBuilder();
        }
      });
  }

  /** Footer Delete (edit only) — opens the confirm modal (Figma 25309 footer). */
  public readonly isConfirmDeleteModalOpen = signal(false);

  public handleClickDeleteCTA(): void {
    if (!this.assistant()) return;
    this.isConfirmDeleteModalOpen.set(true);
  }

  /**
   * Confirm-modal action handler: on `delete`, dispatch the existing delete
   * action (effect calls the API + removes it from the store) and close the
   * builder modal, consistent with save/cancel; `cancel` just closes the
   * confirmation.
   */
  public handleConfirmDeleteModalAction(action: string): void {
    this.isConfirmDeleteModalOpen.set(false);
    if ((action as fromSeriousSystem.ModalAction['type']) !== 'delete') return;
    const skill = this.assistant();
    if (!skill) return;
    this.assistantsStore.dispatch(
      AssistantActions.deleteAssistant({ uuid: skill.uuid }),
    );
    this.closeBuilder();
  }

  /**
   * Load the users and groups the invite picker offers. Tied to the component
   * lifetime so an in-flight request can't outlive the page; a failure is
   * captured (not silent) and leaves the picker empty rather than erroring.
   */
  private loadShareableTargets(): void {
    this.usersService
      .usersControllerGetUsersV1()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        sentryCaptureError,
        catchError(() => EMPTY),
      )
      .subscribe((users) => this.allUsers.set(users));
    this.groupsService
      .groupsControllerGetGroupsV1()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        sentryCaptureError,
        catchError(() => EMPTY),
      )
      .subscribe((groups) => this.allGroups.set(groups));
  }

  /**
   * Seed the share dropdown from the skill's current access when editing an
   * owned skill; reset to private otherwise. Maps the access graph back to the
   * three dropdown states (org-wide → public, any direct user/group grant →
   * shared, else private). Group grants are seeded into the picker (visible,
   * removable rows) and re-sent on reconcile: sharing is owner-authoritative
   * replace semantics, not passthrough.
   *
   * Returns the in-flight access read so the calling effect can cancel it via
   * `onCleanup` before a re-run. If the read fails, `shareAccessLoaded` is left
   * `false` so the reconcile leaves the access graph untouched (the skill still
   * saves) — deliberately NOT reset-to-private, which a save would then read as
   * "revoke everything".
   */
  private seedShareState(
    assistant: fromGenerated.AssistantView | undefined,
  ): Subscription | void {
    const currentUserId = this.userId()?.id;
    const isOwnedEditable =
      !!assistant &&
      !!currentUserId &&
      assistant.ownedBy === currentUserId &&
      this.ffShareAssistant().enabled;

    if (!isOwnedEditable) {
      // New skill (nothing to read) or non-owned (panel hidden): owner-only
      // "Only people invited" (no grants) is the correct, fully-known desired
      // state, so the reconcile may proceed (KD-23).
      this.shareOption.set('shared');
      this.shareSelectedUserIds.set([]);
      this.shareSelectedGroupIds.set([]);
      this.shareAccessLoaded.set(true);
      return;
    }

    this.shareAccessLoaded.set(false);
    return this.assistantsService
      .assistantsControllerGetAssistantAccessV1(assistant.uuid)
      .pipe(
        // Capture (not silent) and swallow: on failure `next` never fires, so
        // `shareAccessLoaded` stays false and the reconcile leaves access
        // untouched.
        sentryCaptureError,
        catchError(() => EMPTY),
      )
      .subscribe((access) => {
        this.shareSelectedUserIds.set(access.users.map((user) => user.id));
        this.shareSelectedGroupIds.set(access.groups.map((g) => g.id));
        this.shareOption.set(
          accessToShareOption(
            access.orgWide,
            access.users.length,
            access.groups.length,
          ),
        );
        this.shareAccessLoaded.set(true);
      });
  }

  /**
   * Reconcile the skill's share state after it has been saved. Best-effort: the
   * skill is already persisted, so a sharing failure is shown as a toast but
   * does not fail the save. No-op when the share panel is not shown. Maps the
   * dropdown: public → org-wide, shared → the picked users + groups, private →
   * neither. Sharing is owner-authoritative replace semantics: the full desired
   * set is sent and any grant not in it is removed (no passthrough).
   */
  private async reconcileShareAccess(uuid: string): Promise<void> {
    // Skip when the panel isn't shown, or when current access hasn't loaded —
    // reconciling the empty defaults would silently revoke existing grants.
    if (!this.isShareSectionVisible() || !this.shareAccessLoaded()) {
      return;
    }
    const isShared = this.shareOption() === 'shared';
    try {
      await firstValueFrom(
        this.assistantsService.assistantsControllerSetAssistantAccessV1(uuid, {
          userIds: isShared ? this.shareSelectedUserIds() : [],
          groupIds: isShared ? this.shareSelectedGroupIds() : [],
          orgWide: this.shareOption() === 'public',
        }),
      );
      // eslint-disable-next-line sonarjs/no-ignored-exceptions
    } catch (_error) {
      this.toastrService.error(
        this.translateService.instant('ASSISTANTS.SHARE.SAVE_ERROR') as string,
        undefined,
        { closeButton: true, disableTimeOut: true },
      );
    }
  }

  // ---------------------------------------------------------------------------
  // DTO builders — read directly from form values, no intermediate cache
  // ---------------------------------------------------------------------------

  private getBasicFormValues(
    locale: BuilderLocale,
  ): AssistantBuilderBasicFormValues {
    return (
      this.basicFormComponent()?.getValues(locale) ?? {
        name: '',
        description: '',
        introMessage: '',
        logo: ASSISTANT_CUSTOM_ICONS[0],
      }
    );
  }

  /**
   * Raw trait form values. For compliance, pass locale for per-locale labels.
   */
  private getTraitFormValues(
    locale?: BuilderLocale,
  ):
    | AssistantBuilderTraitComparisonFormValues
    | AssistantBuilderTraitComplianceFormValues
    | AssistantBuilderTraitGenericFormValues
    | Record<string, unknown> {
    if (this.selectedType().type === TypeOptions.compliance.type) {
      const slot = locale ?? this.activeLocales()[0] ?? 'und';
      const form = this.complianceFormFor(slot)?.form();
      return form?.getRawValue() ?? {};
    }
    return this.traitFormComponent()?.form().getRawValue() ?? {};
  }

  private buildTranslation(locale: BuilderLocale) {
    const v = this.getBasicFormValues(locale);
    const traitValues = this.getTraitFormValues(locale);
    let traitConfigTranslation: object = {};

    if (this.selectedType().type === TypeOptions.compliance.type) {
      const cv = traitValues as AssistantBuilderTraitComplianceFormValues;
      // KD-21/D15: the customize-text labels are OPTIONAL and decoupled from the
      // reference toggle. A blank field persists the per-language default so the
      // usage-side CTA still renders sensible text; a custom value persists
      // verbatim. Materialize-on-save keeps the stored shape unchanged.
      const defaultFor = (key: string): string => {
        const translations = this.translateStore.getTranslations(locale);
        const fromStore = translations
          ? (getValue(translations, key) as string | undefined)
          : undefined;
        return fromStore ?? (this.translateService.instant(key) as string);
      };
      const labelOrDefault = (
        value: string | undefined,
        key: string,
      ): string => (value?.trim() ? value : defaultFor(key));

      traitConfigTranslation = {
        labelForExpectedDocuments: {
          referenceLabel: labelOrDefault(
            cv.referenceLabel,
            'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.REFERENCE_LABEL.DEFAULT',
          ),
          regularLabel: labelOrDefault(
            cv.regularLabel,
            'ASSISTANTS.SAVE_MODAL.FORM.COMPLIANCE.REGULAR_LABEL.DEFAULT',
          ),
        },
      };
    }

    return {
      // The persisted locale (KD-A/C): fr/en when multilingual, else `und` for a
      // new/universal skill or the skill's existing single stored locale.
      locale: this.persistLocaleFor(locale),
      name: v.name,
      description: v.description,
      // Required (non-empty) on create since PR-2 (T5) — the create-mode
      // validators enforce it before Save enables; the update builder
      // overrides it with null when blank (grandfathered optional intro).
      introMessage: v.introMessage ?? '',
      traitConfigTranslation,
    };
  }

  /**
   * Files to persist, tagged with their document role (T4/KD-15): the
   * skill.md configuration file is `instruction` for ALL skill.md traits —
   * generic included — compliance knowledge files are `reference`, and
   * everything else (insight files, generic sourceFiles) is `regular`.
   * The UI stamp is belt-and-suspenders payload clarity only: PR-2's server
   * re-derives `instruction` from the skill.md authoritatively and rejects a
   * client-supplied `instruction` on any other document.
   */
  private getFiles(): RoleTaggedFile[] {
    // Non-translatable fields (files/configurationFile) are shared across the
    // compliance slots, so read from the primary active slot (und OFF, fr ON).
    const traitValues = this.getTraitFormValues();
    const type = this.selectedType().type;
    const tagFiles = (
      files: { id: string; name: string }[] | undefined,
      role: fromGenerated.DocumentRole,
    ): RoleTaggedFile[] =>
      (files ?? []).map((file) => ({ id: file.id, name: file.name, role }));

    const taggedFiles: RoleTaggedFile[] = [];

    if (type === 'generic') {
      const genericValues = traitValues as {
        sourceFiles?: { id: string; name: string }[];
      };
      taggedFiles.push(...tagFiles(genericValues.sourceFiles, 'regular'));
    }
    if (type === 'compliance') {
      const complianceValues =
        traitValues as Partial<AssistantBuilderTraitComplianceFormValues>;
      taggedFiles.push(...tagFiles(complianceValues.files, 'reference'));
    }
    if (type === 'insight' || type === 'creative') {
      taggedFiles.push(
        ...tagFiles(
          (traitValues as { files?: { id: string; name: string }[] }).files,
          'regular',
        ),
      );
    }
    if ((SKILL_MD_TRAITS as readonly string[]).includes(type)) {
      const configurationValues = traitValues as {
        configurationFile?: { id: string; name: string }[];
      };
      taggedFiles.push(
        ...tagFiles(configurationValues.configurationFile, 'instruction'),
      );
    }

    return taggedFiles;
  }

  /** Whether basic + compliance (if applicable) forms are valid for the slot. */
  private isLocaleValid(locale: BuilderLocale): boolean {
    const basicValid = this.basicFormComponent()?.form(locale).valid;
    const complianceForm = this.complianceFormFor(locale)?.form();
    const complianceValid =
      this.selectedType().type !== TypeOptions.compliance.type ||
      complianceForm?.valid;
    return !!(basicValid && complianceValid);
  }

  /**
   * Builds API traitConfig — behavioral-only (T4/KD-2): the instructions now
   * live in the skill.md, so compliance keeps its two toggles (labels are
   * per-locale translations) and comparison keeps only removePromptInput.
   */
  private buildTraitConfig(): object {
    const values = this.getTraitFormValues();
    if (this.selectedType().type === TypeOptions.compliance.type) {
      const cv = values as AssistantBuilderTraitComplianceFormValues;
      return {
        removePromptInput: cv.removePromptInput,
        userWillUploadReferenceDocuments: cv.userWillUploadReferenceDocuments,
      };
    }
    if (this.selectedType().type === TypeOptions.comparison.type) {
      const cv = values as AssistantBuilderTraitComparisonFormValues;
      return { removePromptInput: cv.removePromptInput };
    }
    return {};
  }

  /**
   * The locale each active UI locale (fr/en) is PERSISTED under (KD-A/C / R1):
   * multilingual ON → itself; OFF → the skill's existing single stored locale
   * (legacy fr/en never gratuitously rewritten) or `und` for a new/universal
   * skill. Drives both the translation rows and the update's localesToDelete.
   */
  private persistLocaleFor(uiLocale: BuilderLocale): 'fr' | 'en' | 'und' {
    if (this.supportMultilingual()) return uiLocale;
    const stored = this.assistant()?.translations ?? [];
    if (stored.length === 1 && stored[0]) {
      const storedLocale = stored[0].locale;
      if (
        storedLocale === 'fr' ||
        storedLocale === 'en' ||
        storedLocale === 'und'
      ) {
        return storedLocale;
      }
    }
    return 'und';
  }

  private buildCreateAssistantDto(): fromGenerated.CreateAssistantDto {
    // Logo is shared across slots; read it from the primary active slot (`und`
    // when OFF, `fr` when ON).
    const primaryValues = this.getBasicFormValues(
      this.activeLocales()[0] ?? 'und',
    );

    const translations = this.activeLocales()
      .filter((uiLocale) => this.isLocaleValid(uiLocale))
      .map((uiLocale) => this.buildTranslation(uiLocale))
      .filter((t) => isValidTranslation(t));

    const insightValues = this.getTraitFormValues() as { prompt?: string };
    const prompt =
      this.selectedType().type === TypeOptions.insight.type
        ? (insightValues.prompt ?? '')
        : '';

    const files = this.getFiles();

    return {
      translations,
      logo: primaryValues.logo,
      ...(prompt ? { prompt } : {}),
      trait: this.selectedType().type,
      // Persisted once at create and immutable (absent from UpdateAssistantDto).
      // Omitted when empty (catalog not loaded) → null → deployment default.
      // Also omitted when the Model control was never shown (flag-off create,
      // KD-6): the basic experience sends no `model`, like the legacy modal.
      ...(this.ffAdvancedAssistantBuilder() && this.selectedModelSlug()
        ? { model: this.selectedModelSlug() }
        : {}),
      traitConfig: this.buildTraitConfig(),
      transactionId: this.fileUploadTransactionId(),
      // Per-file role from the tagged getFiles() result (T4/KD-15): the
      // skill.md is `instruction` for all three skill.md traits; the server
      // re-derives it authoritatively.
      documentsToAdd: files.map((file) => ({
        documentId: file.id,
        filename: file.name,
        role: file.role,
      })),
    };
  }

  private buildUpdateAssistantDto(
    assistant: fromGenerated.AssistantView,
  ): fromGenerated.UpdateAssistantDto {
    // Logo is shared across slots; read it from the primary active slot.
    const primaryValues = this.getBasicFormValues(
      this.activeLocales()[0] ?? 'und',
    );

    const existingDocuments =
      assistant.documents?.filter((doc) => !doc.deletedAt) ?? [];
    const files = this.getFiles();

    const documentIdsToDelete = existingDocuments
      .filter((doc) => !files.some((file) => file.id === doc.uuid))
      .map((doc) => doc.uuid);

    // Per-file role from the tagged getFiles() result (T4/KD-15); add/delete
    // diffing keys on document uuid, unchanged.
    const documentsToAdd = files
      .filter((file) => !existingDocuments.some((doc) => doc.uuid === file.id))
      .map((file) => ({
        documentId: file.id,
        filename: file.name,
        role: file.role,
      }));

    const translations = this.activeLocales()
      .filter((uiLocale) => this.isLocaleValid(uiLocale))
      .map((uiLocale) => {
        const basic = this.getBasicFormValues(uiLocale);
        return {
          ...this.buildTranslation(uiLocale),
          introMessage: (basic.introMessage ?? '').trim()
            ? basic.introMessage
            : null,
        };
      })
      .filter((t) => isValidTranslation(t));

    // localesToDelete (SB-3325 PR-4 / R1): stored locales no longer targeted are
    // deleted so a mode change (und↔fr/en) can't leave stale rows shadowing the
    // new content (the update path is otherwise upsert-only). Target set = the
    // persisted locales of the active UI locales — see the target-set table.
    const targetLocales = new Set<string>(
      this.activeLocales().map((uiLocale) => this.persistLocaleFor(uiLocale)),
    );
    const localesToDelete = (assistant.translations ?? [])
      .map((translation) => translation.locale)
      .filter(
        (locale) => !targetLocales.has(locale),
      ) as fromGenerated.UpdateAssistantDto.LocalesToDeleteEnum[];

    const insightValues = this.getTraitFormValues() as { prompt?: string };
    const prompt =
      this.selectedType().type === TypeOptions.insight.type
        ? (insightValues.prompt ?? '')
        : '';

    return {
      translations,
      logo: primaryValues.logo,
      ...(prompt ? { prompt } : {}),
      traitConfig: this.buildTraitConfig(),
      transactionId: this.fileUploadTransactionId(),
      ...(documentsToAdd.length > 0 ? { documentsToAdd } : {}),
      ...(documentIdsToDelete.length > 0 ? { documentIdsToDelete } : {}),
      ...(localesToDelete.length > 0 ? { localesToDelete } : {}),
    };
  }
}
